(() => {
    'use strict';

    /* ------------------------------------------------------------------
     * Project index toolbar.
     *
     * The whole index is already in the page, so filtering and sorting are a
     * matter of hiding and reordering nodes — no request, no re-decoded images.
     * State is read from the form's own controls, which the server has already
     * stamped, so the script and PHP cannot start out disagreeing.
     * ---------------------------------------------------------------- */

    // Held across inits so the listeners registered for one page can be taken down
    // again when swup swaps in the next.
    let form = null;
    let grid = null;
    let onPageShow = null;

    const destroy = () => {
        if (onPageShow) {
            window.removeEventListener('pageshow', onPageShow);
            onPageShow = null;
        }

        form = null;
        grid = null;
    };

    const init = () => {
        destroy();

        form = document.querySelector('[data-archive-toolbar]');
        grid = document.querySelector('[data-archive-grid]');

        if (!form || !grid) {
            return;
        }

        const counter = form.querySelector('[data-archive-count]');
        const reset = form.querySelector('[data-archive-reset]');
        const empty = document.querySelector('[data-archive-empty]');

        const cards = Array.from(grid.querySelectorAll('.nieuws-item')).map((el, order) => ({
            el,
            order,
            cats: (el.dataset.cats || '').split(' ').filter(Boolean),
            date: el.dataset.date || '',
            year: el.dataset.year || '',
            title: el.dataset.title || '',
        }));

        if (!cards.length) {
            return;
        }

        // Submitting is the no-JS path; with JS the work happens here instead.
        form.classList.add('is-live');

        const collator = new Intl.Collator(document.documentElement.lang || 'nl', {
            numeric: true,
            sensitivity: 'base',
        });

        const comparators = {
            'date-desc': (a, b) => b.date.localeCompare(a.date) || a.order - b.order,
            'date-asc': (a, b) => a.date.localeCompare(b.date) || a.order - b.order,
            'title-asc': (a, b) => collator.compare(a.title, b.title),
            'title-desc': (a, b) => collator.compare(b.title, a.title),
        };

        const readState = () => {
            const data = new FormData(form);

            return {
                cats: data.getAll('project_cat[]'),
                year: data.get('project_year') || '0',
                sort: data.get('project_sort') || 'date-desc',
            };
        };

        const matches = (card, state) => {
            if (state.cats.length && !card.cats.some((slug) => state.cats.indexOf(slug) !== -1)) {
                return false;
            }

            return state.year === '0' || card.year === state.year;
        };

        const apply = () => {
            const state = readState();
            const compare = comparators[state.sort] || comparators['date-desc'];
            const fragment = document.createDocumentFragment();
            let shown = 0;

            // Reorder the real nodes rather than using CSS order: reading order and tab
            // order have to match what the eye sees.
            cards.slice().sort(compare).forEach((card) => {
                const ok = matches(card, state);
                card.el.hidden = !ok;

                if (ok) {
                    shown++;
                }

                fragment.appendChild(card.el);
            });

            grid.appendChild(fragment);

            if (counter) {
                const template = counter.dataset.template || '%1$d / %2$d';
                counter.textContent = template
                    .replace('%1$d', String(shown))
                    .replace('%2$d', String(cards.length));
            }

            if (empty) {
                empty.hidden = shown !== 0;
            }

            const pristine = !state.cats.length && state.year === '0' && state.sort === 'date-desc';

            if (reset) {
                reset.hidden = pristine;
            }

            // Keep the URL shareable without pushing history: filtering a list is not
            // navigation, and Back should leave the page, not undo eleven toggles.
            const params = new URLSearchParams();
            state.cats.forEach((slug) => params.append('project_cat[]', slug));

            if (state.year !== '0') {
                params.set('project_year', state.year);
            }

            if (state.sort !== 'date-desc') {
                params.set('project_sort', state.sort);
            }

            const query = params.toString();
            window.history.replaceState(null, '', query ? '?' + query : window.location.pathname);
        };

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            apply();
        });

        form.addEventListener('change', apply);

        if (reset) {
            reset.addEventListener('click', (event) => {
                event.preventDefault();
                form.reset();
                form.querySelectorAll('input[type="checkbox"]').forEach((box) => {
                    box.checked = false;
                });

                const year = form.querySelector('[name="project_year"]');
                if (year) {
                    year.value = '0';
                }

                form.querySelector('[name="project_sort"]').value = 'date-desc';
                apply();
            });
        }

        // Safari restores control values from the bfcache but not our node order.
        onPageShow = apply;
        window.addEventListener('pageshow', onPageShow);

        apply();
    };

    window.MB = window.MB || {};
    window.MB.archiveToolbar = { init, destroy };

    init();
})();
